import { useMediaDownload } from "@/api/media";
import { useNNEvidenceInvalids, useNNEvidences } from "@/api/nn-evidence";
import { useNNEvidenceValidate, useRFS, useRFSStatusUpdate } from "@/api/rfs";
import { FieldFile } from "@/components/form/field-file";
import { FormContainer } from "@/components/form/form-container";
import {
  DefaultButton,
  DownloadButton,
  SubmitButton,
} from "@/components/ui/button";
import { Gaps, VerticalGaps } from "@/components/ui/container";
import { DataInfo, DataInfoWithBadge } from "@/components/ui/data-info";
import { H2, P } from "@/components/ui/typography";
import {
  MISSING_ID_IN_ROUTE,
  REQUIRED_MESSAGE,
  ROLE,
  STATUS,
} from "@/utils/constant";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";
import { axiosInstance } from "@/utils/lib/axios";
import { swalError, swalErrorDefault, swalSuccess } from "@/utils/lib/swal";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent } from "@mui/material";
import { Indicator, Progress } from "@radix-ui/react-progress";
import { delay } from "msw";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { z } from "zod";
import { useCurrentUser } from "../auth/useCurrentUser";
import { compressKmzImageCompressor } from "@/utils/helper/ImageCompressor";
import Swal from "sweetalert2";
import NNEvidenceValidations from "./NNEvidenceValidations";
import { RequiredAsterisk } from "@/components/form/required-asterisk";
import NNEvidenceTableValidations from "./NNEvidenceTableValidations";
import { createRFSStatusUpdater } from "../opm/utils/rfsStatusUpdater";
import { showDummyHouseNumbersAlert } from "../rfs/document/utils/validation";
import { useValidationTimer } from "./hooks/useValidationTimer";
import useCancelNnEvidence from "./hooks/useCancelNnEvidence";
import { AxiosResponse } from "axios";

const nnEvidenceFormSchema = z.object({
  fileList: z
    .instanceof(FileList)
    .refine((value) => value.length > 0, {
      message: REQUIRED_MESSAGE,
    })
    .refine(
      (value) => {
        for (const file of value) {
          if (!file.name.endsWith(".kmz")) {
            return false;
          }
        }
        return true;
      },
      {
        message: "Please upload files in .kmz format only",
      },
    ),
});
type NNEvidenceFormSchema = z.infer<typeof nnEvidenceFormSchema>;

export function NNEvidenceCreateForm() {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [statusValidation, setStatusValidation] = useState("Unvalidated");
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { id } = useParams();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  const { roles } = useCurrentUser();

  const { data: rfs } = useRFS({ id });
  const { data: nnEvidences } = useNNEvidences({ rfsId: id });
  const { data: nnEvidenceInvalids } = useNNEvidenceInvalids({ rfsId: id });
  const { mutate: mutateNNEvidence } = useNNEvidenceValidate();
  const { mutate: downloadMedia, isPending: isDownloading } =
    useMediaDownload();
  const { mutate: updateStatus } = useRFSStatusUpdate();

  const nnEvidence = nnEvidences?.[0];
  const rfsStatus = rfs?.statusRfsSubmission;

  const isShowDummyAlert = showDummyHouseNumbersAlert(nnEvidence);
  const isAllowed =
    hasCommonElement(roles, [
      ROLE.ADMINISTRATOR,
      ROLE.REVISE_DOCUMENT_OFFICER,
      ROLE.VENDOR,
      ROLE.VALIDATOR_DOCUMENT_OFFICER,
    ]) && !isShowDummyAlert;
  const checkingProfileId = rfs?.trackerdataApd?.checkingProfile?.id;
  const isValidating =
    nnEvidence?.statusValidation === STATUS.VALIDATION.IN_PROGRESS;

  const isMoreThanMasterParam = useValidationTimer({
    isValidating,
    updatedDate: nnEvidence?.updatedDate,
  });

  const shouldShowCancelButton = isValidating && isMoreThanMasterParam;

  const form = useForm<NNEvidenceFormSchema>({
    resolver: zodResolver(nnEvidenceFormSchema),
  });

  const handleStatus = useCallback(() => {
    const statusUpdater = createRFSStatusUpdater({
      rfsId: id,
      overallStatus: isValidating ? "Validating" : statusValidation,
      rfsData: rfs,
      updateStatusMutation: updateStatus,
      prefix: "evidenceNn",
      options: {
        onError: () => {
          swalErrorDefault.fire({
            title: "Status Update Failed",
            text: "You failed to update the RFS status. Please try again.",
          });
        },
      },
    });
    statusUpdater();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, statusValidation, updateStatus, nnEvidence, isValidating]);

  // Effect untuk update status setelah validation context berubah
  useEffect(() => {
    if (nnEvidence) {
      // Clear timeout sebelumnya jika ada
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set timeout baru untuk delay 1 detik
      debounceTimerRef.current = setTimeout(() => {
        handleStatus();
      }, 1000);
    }

    // Cleanup function untuk clear timeout saat component unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusValidation, nnEvidence, isValidating]); // Trigger ketika overallStatus berubah

  function handleDownload() {
    if (!nnEvidence || !rfs) {
      throw new Error("NN Evidence or RFS is not found");
    }
    downloadMedia(
      {
        path: nnEvidence.documentNnEvidencePath,
        fileName: `EVIDENCE-${rfs.clusterId}.kmz`,
      },
      {
        onError: () => {
          swalErrorDefault.fire();
        },
      },
    );
  }

  async function handleSubmit(values: NNEvidenceFormSchema) {
    Swal.fire({
      title: "Please wait!",
      text: "Compressing KMZ file...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const compressedFile: FileList = await compressKmzImageCompressor(
        values.fileList[0],
      );
      values.fileList = compressedFile;

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "KMZ file compressed successfully!",
        timer: 2000,
        showConfirmButton: false,
      }).then(() => {
        setOpenDialog(true);
        axiosInstance
          .get("/api/crud/media/emitter", {
            responseType: "stream",
            headers: {
              Accept: "text/event-stream",
            },
            adapter: "fetch",
          })
          .then((response) => onSuccessMutate(response, values));
      });
    } catch (error) {
      Swal.close();
      swalError.fire({
        title: "Compression Failed",
        text: "Failed to compress KMZ file. The file may be corrupted or in an invalid format.",
      });
    }
  }

  const onSuccessMutate = async function (
    response: AxiosResponse,
    values: NNEvidenceFormSchema,
  ) {
    const readableStream = response.data;
    const reader = readableStream
      .pipeThrough(new TextDecoderStream())
      .getReader();

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      const splitValue = value.split("\n");
      const event = splitValue[0].replace("event:", "");

      if (event === "sse_id") {
        const sseId = splitValue[1].replace("data:", "").replace(/"/g, "");

        axiosInstance
          .postForm("/api/crud/media/upload/temp", {
            file: values.fileList[0],
            properties: JSON.stringify({ sse_id: sseId }),
            target: "/nnevidence-document",
          })
          .then(async function (result) {
            if (!id) {
              throw new Error(MISSING_ID_IN_ROUTE);
            }

            const data = await result.data.data;
            onMutateNNEvidence(data);
          })
          .catch(() => {
            setOpenDialog(false);
            setUploadProgress(0);
            swalError.fire({
              title: "Validation Failed",
              text: "You failed to perform data validation. Please check the NN Evidence (kmz) the try again.",
            });
          });
      } else {
        const lines = value.split("\n");
        for (const line of lines) {
          const match = line.match(/data:(\d+)/);
          if (match) {
            const currentNumber = parseInt(match[1], 10);
            setUploadProgress(currentNumber);
          }
        }
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onMutateNNEvidence = (data: any) =>
    mutateNNEvidence(
      {
        id,
        data: {
          nnEvidence: {
            media: {
              id: data.id,
              path: data.path,
              originalFileName: data.originalFileName,
              name: data.name,
            },
          },
        },
      },
      {
        onSuccess: () => {
          delay(1250).then(() => {
            setOpenDialog(false);
            setUploadProgress(0);
            swalSuccess.fire({
              title: "Validation Started",
              text: "You have successfully started the validation process.",
            });
          });
        },
        onError: () => {
          setOpenDialog(false);
          setUploadProgress(0);
          swalError.fire({
            title: "Validation Failed",
            text: "You failed to perform data validation. Please check the NN Evidence (kmz) the try again.",
          });
        },
      },
    );

  const { cancel: handleCancelNnEvidence, isPending: isCancellingNnEvidence } =
    useCancelNnEvidence({ id: nnEvidence?.id ?? "" });

  return (
    <>
      <Dialog
        open={openDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent className="flex h-[348px] w-[512px] flex-col items-center justify-center gap-2">
          <div className="lds-dual-ring"></div>
          <p className="mt-6 text-3xl font-semibold text-[#545454] no-underline">
            In Progress
          </p>
          <p className="mt-2 text-xl text-[#545454]">
            Please wait, starting the validation
          </p>
          <Progress
            className="mt-6 h-7 w-full overflow-hidden border border-black bg-white"
            value={uploadProgress}
          >
            <Indicator
              className="ProgressIndicator"
              style={{ transform: `translateX(-${100 - uploadProgress}%)` }}
            />
          </Progress>
          <P className="text-center text-black">{`${0 + uploadProgress}`}%</P>
        </DialogContent>
      </Dialog>

      <H2>NN Evidence - {rfs?.clusterId}</H2>
      <Gaps className="flex-wrap justify-between">
        <VerticalGaps className="flex-1">
          <DataInfo label="Cluster ID" value={rfs?.clusterId} />
          <DataInfo label="Project Name" value={rfs?.projectName} />
          <NNEvidenceValidations
            checkingProfileId={checkingProfileId}
            nnEvidence={nnEvidence}
            nnEvidenceInvalids={nnEvidenceInvalids}
            setStatusValidation={setStatusValidation}
          />
        </VerticalGaps>
        <VerticalGaps className="flex-none">
          <DataInfoWithBadge
            label="Status"
            value={isValidating ? STATUS.DOCUMENT.VALIDATING : statusValidation}
          />
          <DownloadButton
            disabled={!nnEvidence}
            onClick={handleDownload}
            loading={isDownloading}
          >
            Download NN Evidence (kmz)
          </DownloadButton>
        </VerticalGaps>
      </Gaps>
      <FormContainer form={form} onSubmit={handleSubmit}>
        <FieldFile
          label="Upload NN Evidence (kmz)"
          name="fileList"
          isDisabled={!isAllowed || rfsStatus === STATUS.RFS.REVISION_BY_LN}
          title={getTooltipTitle(isAllowed, rfsStatus)}
          defaultName={nnEvidence?.documentNnEvidenceName}
        />

        <div className="flex gap-x-2">
          <SubmitButton
            disabled={
              !isAllowed ||
              rfsStatus === STATUS.RFS.REVISION_BY_LN ||
              rfsStatus === STATUS.RFS.GENERATING_HPDB ||
              isValidating
            }
            title={getTooltipTitle(isAllowed, rfsStatus)}
          />
          {shouldShowCancelButton && (
            <DefaultButton
              type="button"
              mood="axiata-red"
              loading={isCancellingNnEvidence}
              onClick={handleCancelNnEvidence}
            >
              Cancel
            </DefaultButton>
          )}
        </div>
      </FormContainer>

      {nnEvidenceInvalids && nnEvidenceInvalids.length > 0 && !isValidating && (
        <NNEvidenceTableValidations rfsInvalids={nnEvidenceInvalids} />
      )}
      {isValidating && (
        <span className="text-sm italic">
          <RequiredAsterisk />
          refresh the browser to check last status of validation
        </span>
      )}
    </>
  );
}

function getTooltipTitle(isAllowed: boolean, rfsStatus?: string) {
  if (rfsStatus === STATUS.RFS.REVISION_BY_LN) {
    return "In the review process";
  }
  if (!isAllowed) {
    return "You are not allowed to upload";
  }
}
